import { Box, Switch, Typography } from '@material-ui/core';
import { isEqual } from 'lodash';
import { reaction } from 'mobx';
import { useObserver } from 'mobx-react';
import React from 'react';
import { FB, FBMediaProps, FBMPIProcedureItem, FBMPIProcedureState, FBProcedureItemType, FBProcedureValue, FBProvider, FBTextEditor } from '..';
import { translate } from '../../../common/intl';
import { getInnerHTMLProp } from '../../../common/utils/helpers';
import useTextEditorStyles from '../FBTextEditor/FBTextEditor.styles';
import { htmlWordDiff } from '../helpers/htmlWordDiff';
import useDeviationProcedureStyles from './FBDeviationProcedure.styles';
import { FBDeviationStepValue } from './FBDeviationProcedure.types';

interface StepProps {
  mpiId: string
  stepId: string
  stepData: FBDeviationStepValue
  name: string
  disabled?: boolean
  isPreview?: boolean
  isOutput?: boolean
  isDeviated?: boolean
  mpiProcedureState: FBMPIProcedureState
}

const FBDeviationProcedureStep: React.FC<StepProps> = ({
  mpiId,
  stepId,
  stepData,
  name,
  disabled,
  isPreview,
  isDeviated,
  mpiProcedureState,
}) => {
  const classes = useDeviationProcedureStyles();
  const editorClasses = useTextEditorStyles();
  const { formState } = FB.useStores();
  const stepName = `${name}.${mpiId}.${stepId}`;
  const [showDiff, setShowDiff] = React.useState(false);

  // The procedure as released in the MPI (snapshot); an empty procedure has no editorConfig.
  // Old snapshots carry no schemaItem at all, so they get no diff
  const original = stepData.schemaItem
    ? (stepData.schemaItem.editorConfig ?? {}) as FBProcedureValue
    : undefined;
  // MPI procedures usually have no label of their own: the MPI shows the control name ("PI Procedure")
  const label = stepData.label || (stepData.schemaItem?.label as string | undefined)
    || translate('form.builder.mpiprocedure');
  const diffOn = showDiff && Boolean(original);

  // Seed form state synchronously on first render so FBTextEditor (withState)
  // picks up the existing description before it initializes its internal state.
  const seeded = React.useRef(false);
  if (!seeded.current) {
    seeded.current = true;
    if (stepData.description && !formState?.getFieldValue(`${stepName}.description`)) {
      formState?.setFieldValue(`${stepName}.description`, stepData.description);
    }
  }

  React.useEffect(() => reaction(
    () => formState?.inputState.get(`${stepName}.description`)?.value,
    (desc) => {
      mpiProcedureState.value = { ...mpiProcedureState.value, description: desc as string };
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ), []);

  React.useEffect(() => reaction(
    () => formState?.inputState.get(`${stepName}.attachments`)?.value,
    (attachments) => {
      const { attachments: stateAttachments } = mpiProcedureState.value ?? {};
      const incoming = (attachments as FBMediaProps[]) ?? [];
      // eslint-disable-next-line @typescript-eslint/require-array-sort-compare
      if (isEqual([...incoming].sort(), [...(stateAttachments ?? [])].sort())) { return; }
      mpiProcedureState.value = { ...mpiProcedureState.value, attachments: incoming };
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ), []);

  const diffHtml = useObserver(() => (diffOn
    ? htmlWordDiff(original?.description, mpiProcedureState.value?.description ?? stepData.description)
    : ''));
  const addedAttachmentIds = useObserver(() => (diffOn
    ? new Set((mpiProcedureState.value?.attachments ?? [])
      .filter((a) => !(original?.attachments ?? []).some((b) => b.id === a.id))
      .map((a) => a.id))
    : undefined));

  const itemTypes = [
    FBProcedureItemType.attachments,
    FBProcedureItemType.materials,
    FBProcedureItemType.equipment,
  ];

  return (
    <FBProvider {...{ mpiProcedureState }}>
      <Box mb={2}>
        <Box className={classes.procedureHeader}>
          <Typography className={classes.procedureLabel}>{label}</Typography>
          {isDeviated && original && (
            <Box display="flex" alignItems="center">
              <Switch
                checked={showDiff}
                onChange={() => setShowDiff(!showDiff)}
                color="primary"
                size="small"
              />
              <Typography className={classes.showDiffLabel}>{translate('lhrDeviation.showDiff')}</Typography>
            </Box>
          )}
        </Box>
        <Typography variant="caption" className={classes.stepCaption}>
          {translate('form.builder.step.number', { number: stepData.stepIndex ?? '' })}
        </Typography>
        {diffOn
          ? (
            <div className={editorClasses.root}>
              <div className={editorClasses.previewContent} dangerouslySetInnerHTML={getInnerHTMLProp(diffHtml)} />
            </div>
          )
          : (
            <FBTextEditor
              name={`${stepName}.description`}
              gutter={false}
              autosave={false}
              shouldNotAutosaveOnBlur={true}
              withState
              disabled={false}
              removeBorder={false}
            />
          )}
        {itemTypes.map((type) => (
          <Box key={type} mt={2}>
            <FBMPIProcedureItem
              {...{ type, name: stepName, disabled, isPreview }}
              isDeviationMode={true}
              isDocumentMPI={true}
              hideDeviationChanges={!diffOn}
              baseEditorConfig={diffOn ? original : undefined}
              deviationAttachmentIds={type === FBProcedureItemType.attachments ? addedAttachmentIds : undefined}
              value={type === FBProcedureItemType.attachments ? undefined : mpiProcedureState[type]}
              // like the MPI: the attachments panel opens and loads its files from the step's attachments
              defaultValue={type === FBProcedureItemType.attachments
                ? (mpiProcedureState.value?.attachments ?? stepData.attachments)
                : undefined}
              handleRemove={(id: string) => mpiProcedureState.removeItem(id, type)}
              getItem={(id: string) => mpiProcedureState.getItem(id, type) ?? ''}
            />
          </Box>
        ))}
      </Box>
    </FBProvider>
  );
};

export default FBDeviationProcedureStep;
